import React, { createContext, useContext, useReducer, useCallback } from 'react';
import PropTypes from 'prop-types';
import CryptoJS from 'crypto-js';
import safeToast from 'utils/safeToast';
import chatbotService from '../API/chatbotService';

// Chatbot Context
const ChatbotContext = createContext();

// Action types
const CHATBOT_ACTIONS = {
  SET_OPEN: 'SET_OPEN',
  ADD_MESSAGE: 'ADD_MESSAGE',
  SET_TYPING: 'SET_TYPING',
  CLEAR_MESSAGES: 'CLEAR_MESSAGES',
  SET_QUICK_ACTIONS: 'SET_QUICK_ACTIONS',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_CONFIG: 'SET_CONFIG',
};

// Get initial message based on authentication status
const getInitialMessage = () => {
  // Check authentication status
  const userDetails = localStorage.getItem("UserLogDetails");
  const token = localStorage.getItem("token");
  const isAuthenticated = !!(userDetails && token);
  
  if (isAuthenticated) {
    return {
      id: 1,
      text: "Hello! I'm IMIA, your IMPRES Intelligent Assistant. You're authenticated and ready to chat! How can I help you today?",
      isUser: false,
      timestamp: new Date(),
      type: 'text',
      options: [
        { text: "👥 Show me chapters", action: "chapters" },
        { text: "🔍 Search alumni", action: "search_alumni" },
        { text: "📅 Find events", action: "events" },
        { text: "👶 My children", action: "children" },
        { text: "👤 Profile help", action: "profile_help" },
        { text: "❓ General help", action: "general_help" }
      ]
    };
  } else {
    return {
      id: 1,
      text: "Hello! I'm IMIA, your IMPRES Intelligent Assistant. Please login to access all features and start chatting.",
      isUser: false,
      timestamp: new Date(),
      type: 'text',
      options: [
        { text: "🔐 Please login first", action: "login_required" },
        { text: "❓ General help", action: "general_help" }
      ]
    };
  }
};

// Initial state
const initialState = {
  isOpen: false,
  messages: [getInitialMessage()],
  isTyping: false,
  showQuickActions: true,
  isLoading: false,
  error: null,
  config: {
    apiEndpoint: process.env.REACT_APP_CHATBOT_API || 'https://api.openai.com/v1/chat/completions',
    model: 'gpt-3.5-turbo',
    maxTokens: 500,
    temperature: 0.7,
    systemPrompt: `You are IMIA, the IMPRES Intelligent Assistant for the IMPRES Alumnus platform. You are a simple, friendly assistant that greets users and shows their profile information when they say hello or hi.`,
    quickActions: []
  }
};

// Reducer
const chatbotReducer = (state, action) => {
  switch (action.type) {
    case CHATBOT_ACTIONS.SET_OPEN:
      return { ...state, isOpen: action.payload };
    
    case CHATBOT_ACTIONS.ADD_MESSAGE:
      return { 
        ...state, 
        messages: [...state.messages, action.payload],
        showQuickActions: false 
      };
    
    case CHATBOT_ACTIONS.SET_TYPING:
      return { ...state, isTyping: action.payload };
    
    case CHATBOT_ACTIONS.CLEAR_MESSAGES:
      return { 
        ...state, 
        messages: [action.payload || initialState.messages[0]],
        showQuickActions: true 
      };
    
    case CHATBOT_ACTIONS.SET_QUICK_ACTIONS:
      return { ...state, showQuickActions: action.payload };
    
    case CHATBOT_ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };
    
    case CHATBOT_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload };
    
    case CHATBOT_ACTIONS.SET_CONFIG:
      return { ...state, config: { ...state.config, ...action.payload } };
    
    default:
      return state;
  }
};

// Helper function to check if user is logged in
const isUserLoggedIn = () => {
  try {
    const userDetails = localStorage.getItem("UserLogDetails");
  
    
    // Check if both user details and token exist
    if (!userDetails) {
      return false;
    }
    
    // Try to decrypt user details to verify they're valid
    const AES_KEY = "7D3B8E69F1A84B7EA8A3F6792B44C926";
    const AES_IV = "A1B2C3D4E5F60718";
    
    const key = CryptoJS.enc.Utf8.parse(AES_KEY);
    const iv = CryptoJS.enc.Utf8.parse(AES_IV);
    
    const decrypted = CryptoJS.AES.decrypt(userDetails, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    
    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
    if (!decryptedText) return false;
    
    const parsedUser = JSON.parse(decryptedText);
    return !!(parsedUser.UserName && parsedUser.StudentId);
  } catch (error) {
    console.error("Error checking login status:", error);
    return false;
  }
};

// Helper function to get user information
const getUserInfo = () => {
  try {
    // First check if user is logged in
    if (!isUserLoggedIn()) {
      return null;
    }
    
    const userDetails = localStorage.getItem("UserLogDetails");
    if (!userDetails) return null;
    
    // AES decryption
    const AES_KEY = "7D3B8E69F1A84B7EA8A3F6792B44C926";
    const AES_IV = "A1B2C3D4E5F60718";
    
    const key = CryptoJS.enc.Utf8.parse(AES_KEY);
    const iv = CryptoJS.enc.Utf8.parse(AES_IV);
    const decrypted = CryptoJS.AES.decrypt(userDetails, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    
    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
    const parsedUser = JSON.parse(decryptedText);
    
    return {
      name: parsedUser.UserName || "User",
      studentId: parsedUser.StudentId || "",
      email: parsedUser.Email || ""
    };
  } catch (error) {
    console.error("Error getting user info:", error);
    return null;
  }
};

// Provider component
export const ChatbotProvider = ({ children }) => {
  const [state, dispatch] = useReducer(chatbotReducer, initialState);


  // Actions
  const openChatbot = useCallback(() => {
    dispatch({ type: CHATBOT_ACTIONS.SET_OPEN, payload: true });
  }, []);

  const closeChatbot = useCallback(() => {
    dispatch({ type: CHATBOT_ACTIONS.SET_OPEN, payload: false });
  }, []);

  const addMessage = useCallback((message) => {
    dispatch({ type: CHATBOT_ACTIONS.ADD_MESSAGE, payload: message });
  }, []);

  const setTyping = useCallback((isTyping) => {
    dispatch({ type: CHATBOT_ACTIONS.SET_TYPING, payload: isTyping });
  }, []);

  const clearMessages = useCallback(() => {
    dispatch({ type: CHATBOT_ACTIONS.CLEAR_MESSAGES });
  }, []);

  const setQuickActions = useCallback((show) => {
    dispatch({ type: CHATBOT_ACTIONS.SET_QUICK_ACTIONS, payload: show });
  }, []);

  const setLoading = useCallback((isLoading) => {
    dispatch({ type: CHATBOT_ACTIONS.SET_LOADING, payload: isLoading });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: CHATBOT_ACTIONS.SET_ERROR, payload: error });
  }, []);

  const updateConfig = useCallback((config) => {
    dispatch({ type: CHATBOT_ACTIONS.SET_CONFIG, payload: config });
  }, []);

  // Refresh initial message based on current authentication status
  const refreshInitialMessage = useCallback(() => {
    const newInitialMessage = getInitialMessage();
    dispatch({ 
      type: CHATBOT_ACTIONS.CLEAR_MESSAGES,
      payload: newInitialMessage
    });
  }, []);

  // Check if user authentication status has changed and refresh if needed
  const checkAndRefreshAuth = useCallback(() => {
    const currentAuth = isUserLoggedIn();
    const currentMessage = state.messages[0];
    
    // If authentication status changed, refresh the initial message
    if (currentMessage && currentMessage.id === 1) {
      const isCurrentlyAuthenticated = currentMessage.text.includes("You're authenticated");
      if (currentAuth !== isCurrentlyAuthenticated) {
        refreshInitialMessage();
      }
    }
  }, [state.messages, refreshInitialMessage]);

  // AI Response generation
  const generateAIResponse = useCallback(async (userMessage, context = {}) => {
    setTyping(true);
    setError(null);

    try {
      // Check if we have API key and endpoint configured
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      
      if (!apiKey) {
        // Fallback to rule-based responses
        return await generateFallbackResponse(userMessage, context);
      }

      // Call OpenAI API
      const response = await fetch(state.config.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: state.config.model,
          messages: [
            { role: 'system', content: state.config.systemPrompt },
            { role: 'user', content: userMessage }
          ],
          max_tokens: state.config.maxTokens,
          temperature: state.config.temperature,
        }),
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();
      const aiMessage = data.choices[0]?.message?.content || 'I apologize, but I could not generate a response.';

      setTyping(false);
      return aiMessage;

    } catch (error) {
      console.error('AI API Error:', error);
      setError(error.message);
      setTyping(false);
      
      // Fallback to rule-based response
      return await generateFallbackResponse(userMessage, context);
    }
  }, [state.config, setTyping, setError]);

  // Fallback response generation
  const generateFallbackResponse = useCallback(async (userMessage, context = {}) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));
    
    const lowerMessage = userMessage.toLowerCase();
    
    // Handle hello/hi greetings with user info
    if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey') || lowerMessage.includes('greetings')) {
      const isLoggedIn = isUserLoggedIn();
      const userInfo = isLoggedIn ? getUserInfo() : null;
      
      if (isLoggedIn && userInfo) {
        return {
          text: `Hello ${userInfo.name}! 👋 Welcome to the IMPRES Alumnus platform. I'm IMIA, your intelligent assistant. I can see you're logged in as ${userInfo.name}. How can I help you today?`,
          options: [
            { text: "👥 Show me chapters", action: "chapters" },
            { text: "🔍 Search alumni", action: "search_alumni" },
            { text: "📅 Find events", action: "events" },
            { text: "👶 My children", action: "children" },
            { text: "👤 Profile help", action: "profile_help" },
            { text: "❓ General help", action: "general_help" }
          ]
        };
      } else {
        return {
          text: `Hello! Welcome to the IMPRES Alumnus platform. I'm IMIA, your intelligent assistant. Please login to access personalized features and continue with your alumni journey.`,
          options: [
            { text: "🔐 Please login first", action: "login_required" },
            { text: "👥 Show me chapters", action: "chapters" },
            { text: "❓ General help", action: "general_help" }
          ]
        };
      }
    }
    // Enhanced rule-based responses
    else if (lowerMessage.includes('search') || lowerMessage.includes('find') || lowerMessage.includes('alumni')) {
      const isLoggedIn = isUserLoggedIn();
      if (!isLoggedIn) {
        return {
          text: `I can help you search for alumni, but you need to login first to access this feature. Please login to continue.`,
          options: [
            { text: "🔐 Please login first", action: "login_required" },
            { text: "👥 Show me chapters", action: "chapters" },
            { text: "❓ General help", action: "general_help" }
          ]
        };
      }
      return `I can help you search for alumni! You can search by:
      • Name
      • Batch year
      • Course studied
      • Organization
      • Location
      
      What specific information are you looking for? I can guide you through the search process.`;
    } 
    else if (lowerMessage.includes('event') || lowerMessage.includes('meeting') || lowerMessage.includes('gathering')) {
      const isLoggedIn = isUserLoggedIn();
      if (!isLoggedIn) {
        return {
          text: `I can help you with event information, but you need to login first to access this feature. Please login to continue.`,
          options: [
            { text: "🔐 Please login first", action: "login_required" },
            { text: "👥 Show me chapters", action: "chapters" },
            { text: "❓ General help", action: "general_help" }
          ]
        };
      }
      
      // Fetch events from API
      try {
        return await getEventsDetails();
      } catch (error) {
        console.error('Error fetching events:', error);
        return `I'm having trouble fetching event information right now. Please try again later.`;
      }
    } 
    else if (lowerMessage.includes('chapter') || lowerMessage.includes('group') || lowerMessage.includes('regional') || lowerMessage.includes('chapters')) {
      const isLoggedIn = isUserLoggedIn();
      if (!isLoggedIn) {
        return {
          text: `I can help you with chapter information, but you need to login first to access this feature. Please login to continue.`,
          options: [
            { text: "🔐 Please login first", action: "login_required" },
            { text: "❓ General help", action: "general_help" }
          ]
        };
      }
      
      // Check if user is asking about a specific chapter
      const specificChapterPatterns = [
        'bangalore', 'blr', 'chennai', 'chn', 'mumbai', 'delhi', 'hyderabad', 'pune', 'kolkata'
      ];
      
      const isSpecificChapter = specificChapterPatterns.some(pattern => 
        lowerMessage.includes(pattern)
      );
      
      if (isSpecificChapter) {
        // Extract chapter name from the message
        const chapterName = specificChapterPatterns.find(pattern => 
          lowerMessage.includes(pattern)
        );
        return await getChapterDetails(chapterName);
      } else {
        // Fetch all chapters from API
        try {
          const chapters = await chatbotService.getChapters();
          if (chapters && chapters.length > 0) {
            let response = `Here are the available alumni chapters:\n\n`;
            chapters.forEach((chapter, index) => {
              response += `**${chapter.chapterName}** (${chapter.chapterShortName})\n`;
              response += `📍 Location: ${chapter.location}\n`;
              response += `📝 Description: ${chapter.description}\n`;
              response += `✅ Status: ${chapter.isActive ? 'Active' : 'Inactive'}\n`;
              response += `📅 Created: ${new Date(chapter.createdAt).toLocaleDateString()}\n\n`;
            });
            response += `Would you like more details about any specific chapter? Just ask about "Bangalore Chapter" or "Chennai Chapter" for example.`;
            return response;
          } else {
            return `I couldn't find any chapters at the moment. Please try again later or contact support.`;
          }
        } catch (error) {
          console.error('Error fetching chapters:', error);
          return `I'm having trouble fetching chapter information right now. Please try again later.`;
        }
      }
    } 
    else if (lowerMessage.includes('children') || lowerMessage.includes('child') || lowerMessage.includes('family') || lowerMessage.includes('kids')) {
      const isLoggedIn = isUserLoggedIn();
      if (!isLoggedIn) {
        return {
          text: `I can help you with children details, but you need to login first to access this feature. Please login to continue.`,
          options: [
            { text: "🔐 Please login first", action: "login_required" },
            { text: "👥 Show me chapters", action: "chapters" },
            { text: "❓ General help", action: "general_help" }
          ]
        };
      }
      
      // Fetch children details from API
      try {
        return await getChildrenDetails();
      } catch (error) {
        console.error('Error fetching children details:', error);
        return `I'm having trouble fetching your children details right now. Please try again later.`;
      }
    }
    else if (lowerMessage.includes('profile') || lowerMessage.includes('update') || lowerMessage.includes('details') || lowerMessage.includes('information')) {
      const isLoggedIn = isUserLoggedIn();
      if (!isLoggedIn) {
        return {
          text: `I can help you with your profile, but you need to login first to access this feature. Please login to continue.`,
          options: [
            { text: "🔐 Please login first", action: "login_required" },
            { text: "👥 Show me chapters", action: "chapters" },
            { text: "❓ General help", action: "general_help" }
          ]
        };
      }
      return `To update your profile information:
      1. Go to the "My Details" section in the main menu
      2. Click the edit button to modify your information
      3. Update any of these fields:
         • Roll Number - Your unique student identifier
         • Name - Your full name
         • Salutation - Mr., Mrs., Dr., etc.
         • Gender - Male, Female, Other
         • Date of Birth - Your birth date
         • Mother Tongue - Your native language
         • Blood Group - A+, B+, O+, AB+, etc.
         • Contact Number - Your phone number
         • Email - Your primary email address
      4. Save your changes
      
      I can help you understand what each field means or guide you through specific updates.`;
    } 
    else if (lowerMessage.includes('roll number') || lowerMessage.includes('roll num') || lowerMessage.includes('rn')) {
      return `Roll Number (rn) is your unique student identifier in the IMPRES system. It's used to:
      • Identify you in the alumni database
      • Link your academic records
      • Verify your alumni status
      
      This field is usually pre-filled and may not be editable. It helps other alumni find you when searching.`;
    }
    else if (lowerMessage.includes('salutation') || lowerMessage.includes('slt') || lowerMessage.includes('mr') || lowerMessage.includes('mrs') || lowerMessage.includes('dr')) {
      return `Salutation (slt) is a title used before your name, such as:
      • Mr. - for men
      • Mrs. - for married women
      • Ms. - for women (married or unmarried)
      • Dr. - for doctors
      • Prof. - for professors
      
      Choose the appropriate salutation from the dropdown menu when editing your profile.`;
    }
    else if (lowerMessage.includes('blood group') || lowerMessage.includes('blg') || lowerMessage.includes('blood type')) {
      return `Blood Group (blg) refers to your blood type classification, such as:
      • A+ or A-
      • B+ or B-
      • AB+ or AB-
      • O+ or O-
      
      This information can be useful for medical emergencies or alumni health initiatives. Select your blood group from the dropdown menu.`;
    }
    else if (lowerMessage.includes('mother tongue') || lowerMessage.includes('mth') || lowerMessage.includes('native language')) {
      return `Mother Tongue (mth) is your native or first language. This helps the platform:
      • Connect you with alumni who speak the same language
      • Organize language-specific events
      • Provide localized content
      
      Select your mother tongue from the available language options in the dropdown.`;
    }
    else if (lowerMessage.includes('explain') && lowerMessage.includes('profile') && lowerMessage.includes('field')) {
      return `Here's a detailed explanation of all profile fields in the IMPRES Alumnus platform:

      **PERSONAL INFORMATION:**
      • **Roll Number (rn)** - Your unique student identifier, usually pre-filled
      • **Name (sn)** - Your full name as it appears on official records
      • **Salutation (slt)** - Title like Mr., Mrs., Dr., Prof. (dropdown selection)
      • **Gender (gn)** - Male, Female, or Other (dropdown selection)
      • **Date of Birth (dobStr)** - Your birth date in DD/MM/YYYY format

      **ADDITIONAL DETAILS:**
      • **Mother Tongue (mth)** - Your native language (dropdown selection)
      • **Blood Group (blg)** - Your blood type like A+, B-, O+, AB- (dropdown selection)

      **CONTACT INFORMATION:**
      • **Contact Number (cn)** - Your phone number (usually pre-filled)
      • **Email 1 (em1)** - Your primary email address (usually pre-filled)

      **IMPORTANT NOTES:**
      - Fields marked with (dropdown selection) have predefined options
      - Contact fields are often pre-filled and may not be editable
      - Roll Number is your unique identifier in the system
      - All fields help other alumni find and connect with you

      Would you like me to explain any specific field in more detail?`;
    }
    else if (lowerMessage.includes('help') || lowerMessage.includes('support') || lowerMessage.includes('assistance')) {
      return `I'm here to help! I can assist you with:
      • Alumni search and connections
      • Event information and registration
      • Chapter details and membership
      • Profile management and field explanations
      • General platform navigation
      
      What specific area would you like help with?`;
    } 
    else if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey') || lowerMessage.includes('greetings')) {
      return `Hello! Welcome to the IMPRES Alumnus platform. I'm your AI assistant and I'm here to help you:
      • Connect with fellow alumni
      • Find and join events
      • Learn about chapters
      • Manage your profile
      • Navigate the platform
      
      How can I assist you today?`;
    } 
    else if (lowerMessage.includes('thank') || lowerMessage.includes('thanks')) {
      return `You're welcome! I'm always here to help. Feel free to ask me anything about the platform or if you need assistance with anything else.`;
    }
    else {
      return `I understand you're asking about: "${userMessage}". 
      
      I can help you with:
      • Alumni search and connections
      • Event information and registration  
      • Chapter details and membership
      • Profile management
      • General platform questions
      
      Could you be more specific about what you need help with?`;
    }
  }, []);

  // Send message function
  const sendMessage = useCallback(async (text, context = {}) => {
    if (!text.trim()) return;

    // Check authentication before allowing chat
    const isLoggedIn = isUserLoggedIn();
    if (!isLoggedIn) {
      const loginRequiredMessage = {
        id: Date.now(),
        text: `🔐 **Authentication Required**

You need to login to chat with IMIA. Please login to your account to access the intelligent assistant features.

**What you can do:**
• Login to your account using the login button in the navigation
• Access basic information about chapters (no login required)
• Get general help about the platform

**After login, you'll be able to:**
• Search for alumni
• Manage your profile
• Access event information
• Get personalized assistance`,
        isUser: false,
        timestamp: new Date(),
        type: 'text',
        options: [
          { text: "🔐 Please login first", action: "login_required" },
          { text: "👥 Show me chapters", action: "chapters" },
          { text: "❓ General help", action: "general_help" }
        ]
      };
      
      addMessage(loginRequiredMessage);
      return;
    }

    const userMessage = {
      id: Date.now(),
      text: text.trim(),
      isUser: true,
      timestamp: new Date(),
      type: 'text'
    };

    addMessage(userMessage);

    try {
      const aiResponse = await generateAIResponse(text, context);
      
      const aiMessage = {
        id: Date.now() + 1,
        text: typeof aiResponse === 'string' ? aiResponse : aiResponse.text,
        isUser: false,
        timestamp: new Date(),
        type: 'text',
        options: typeof aiResponse === 'object' && aiResponse.options ? aiResponse.options : undefined
      };

      addMessage(aiMessage);
    } catch (error) {
      console.error('Error sending message:', error);
      safeToast.error('Failed to send message. Please try again.');
    }
  }, [addMessage, generateAIResponse]);

  // Get specific chapter details
  const getChapterDetails = useCallback(async (chapterName) => {
    try {
      const chapters = await chatbotService.getChapters();
      const chapter = chapters.find(c => 
        c.chapterName.toLowerCase().includes(chapterName.toLowerCase()) ||
        c.chapterShortName.toLowerCase().includes(chapterName.toLowerCase())
      );
      
      if (chapter) {
        return {
          text: `**${chapter.chapterName}** (${chapter.chapterShortName})

📍 **Location:** ${chapter.location}
📝 **Description:** ${chapter.description}
✅ **Status:** ${chapter.isActive ? 'Active' : 'Inactive'}
📅 **Created:** ${new Date(chapter.createdAt).toLocaleDateString()}
${chapter.staffCode ? `🏢 **Staff Code:** ${chapter.staffCode}` : ''}

This chapter is ${chapter.isActive ? 'currently active' : 'currently inactive'} and serves alumni in the ${chapter.location} area. Would you like to know more about joining this chapter or any other information?`,
          options: [
            { text: "📋 Join this chapter", action: `join_chapter_${chapter.chapterId}` },
            { text: "👥 Show all chapters", action: "chapters" },
            { text: "🔍 Search alumni", action: "search_alumni" },
            { text: "❓ More help", action: "general_help" }
          ]
        };
      } else {
        return {
          text: `I couldn't find a chapter matching "${chapterName}". Here are the available chapters:\n\n${chapters.map(c => `• ${c.chapterName} (${c.chapterShortName}) - ${c.location}`).join('\n')}`,
          options: [
            { text: "👥 Show all chapters", action: "chapters" },
            { text: "🔍 Search alumni", action: "search_alumni" },
            { text: "❓ General help", action: "general_help" }
          ]
        };
      }
    } catch (error) {
      console.error('Error fetching chapter details:', error);
      return {
        text: `I'm having trouble fetching details for "${chapterName}". Please try again later.`,
        options: [
          { text: "🔄 Try again", action: "chapters" },
          { text: "❓ General help", action: "general_help" }
        ]
      };
    }
  }, []);

  // Handle option clicks
  const handleOptionClick = useCallback(async (action, messageId) => {
    try {
      // Check authentication for all actions (except general help and login required)
      const publicActions = ['general_help', 'login_required'];
      if (!publicActions.includes(action) && !isUserLoggedIn()) {
        const loginRequiredMessage = {
          id: Date.now(),
          text: `🔐 **Authentication Required**

You need to login to access this feature. Please login to your account first.

**What you can do:**
• Login to your account using the login button in the navigation
• Get general help about the platform

**After login, you'll be able to:**
• View chapter details and information
• Search for alumni
• Manage your profile
• Access event information`,
          isUser: false,
          timestamp: new Date(),
          type: 'text',
          options: [
            { text: "🔐 Please login first", action: "login_required" },
            { text: "❓ General help", action: "general_help" }
          ]
        };
        
        addMessage(loginRequiredMessage);
        return;
      }

      let response;
      let options = [];

      switch (action) {
        case "chapters":
          const chapters = await chatbotService.getChapters();
          if (chapters && chapters.length > 0) {
            response = `Here are the available alumni chapters:\n\n`;
            chapters.forEach((chapter, index) => {
              response += `**${chapter.chapterName}** (${chapter.chapterShortName})\n`;
              response += `📍 Location: ${chapter.location}\n`;
              response += `📝 Description: ${chapter.description}\n`;
              response += `✅ Status: ${chapter.isActive ? 'Active' : 'Inactive'}\n\n`;
            });
            response += `Click on any chapter name below for more details!`;
            options = chapters.map(chapter => ({
              text: `📍 ${chapter.chapterName}`,
              action: `chapter_${chapter.chapterName.toLowerCase().replace(/\s+/g, '_')}`
            })).concat([
              { text: "🔍 Search alumni", action: "search_alumni" },
              { text: "❓ More help", action: "general_help" }
            ]);
          } else {
            response = `I couldn't find any chapters at the moment. Please try again later.`;
            options = [
              { text: "🔄 Try again", action: "chapters" },
              { text: "❓ General help", action: "general_help" }
            ];
          }
          break;

        case "search_alumni":
          response = `I can help you search for alumni! You can search by:\n• Name\n• Batch year\n• Course studied\n• Organization\n• Location\n\nWhat specific information are you looking for?`;
          options = [
            { text: "👥 Show chapters", action: "chapters" },
            { text: "📅 Find events", action: "events" },
            { text: "❓ General help", action: "general_help" }
          ];
          break;

        case "events":
          const eventsDetails = await getEventsDetails();
          response = eventsDetails.text;
          options = eventsDetails.options;
          break;

        case "children":
          const childrenDetails = await getChildrenDetails();
          response = childrenDetails.text;
          options = childrenDetails.options;
          break;

        case "profile_help":
          response = `I can help you with your profile! Here's what you can do:\n• Update personal information\n• Change contact details\n• Modify professional information\n• Understand profile fields\n\nWhat would you like to update?`;
          options = [
            { text: "✏️ Update profile", action: "update_profile" },
            { text: "📋 Profile fields help", action: "profile_fields" },
            { text: "👥 Show chapters", action: "chapters" },
            { text: "❓ General help", action: "general_help" }
          ];
          break;

        case "general_help":
          const isLoggedIn = isUserLoggedIn();
          if (isLoggedIn) {
            response = `I'm here to help! I can assist you with:\n• Chapter details and membership\n• Alumni search and connections\n• Event information and registration\n• Children and family details\n• Profile management\n• General platform navigation\n\nWhat would you like to know?`;
            options = [
              { text: "👥 Show chapters", action: "chapters" },
              { text: "🔍 Search alumni", action: "search_alumni" },
              { text: "👶 My children", action: "children" },
              { text: "📅 Find events", action: "events" },
              { text: "👤 Profile help", action: "profile_help" }
            ];
          } else {
            response = `I'm here to help! However, most features require you to login first.\n\n**Available without login:**\n• General platform information\n• Help and support\n\n**Available after login:**\n• Chapter details and membership\n• Alumni search and connections\n• Event information and registration\n• Profile management\n\nPlease login to access all features!`;
            options = [
              { text: "🔐 Please login first", action: "login_required" }
            ];
          }
          break;

        case "login_required":
          response = `🔐 **Login Required**\n\nTo access all features like:\n• Chapter details and information\n• Alumni search and connections\n• Event registration and management\n• Profile updates and management\n• Personalized recommendations\n\nPlease login to your account first. You can find the login option in the top navigation menu.`;
          options = [
            { text: "❓ General help", action: "general_help" }
          ];
          break;

        default:
          if (action.startsWith("chapter_")) {
            const chapterName = action.replace("chapter_", "").replace(/_/g, " ");
            const chapterDetails = await getChapterDetails(chapterName);
            response = chapterDetails.text;
            options = chapterDetails.options;
          } else {
            response = `I'm not sure how to help with that. Here are some things I can do:`;
            options = [
              { text: "👥 Show chapters", action: "chapters" },
              { text: "🔍 Search alumni", action: "search_alumni" },
              { text: "📅 Find events", action: "events" },
              { text: "❓ General help", action: "general_help" }
            ];
          }
      }

      // Add the response message with options
      const message = {
        id: Date.now(),
        text: response,
        isUser: false,
        timestamp: new Date(),
        type: 'text',
        options: options
      };

      addMessage(message);
    } catch (error) {
      console.error('Error handling option click:', error);
      const errorMessage = {
        id: Date.now(),
        text: `I'm sorry, I encountered an error. Please try again.`,
        isUser: false,
        timestamp: new Date(),
        type: 'text',
        options: [
          { text: "🔄 Try again", action: "general_help" },
          { text: "❓ General help", action: "general_help" }
        ]
      };
      addMessage(errorMessage);
    }
  }, [addMessage, getChapterDetails]);

  // Get events details
  const getEventsDetails = useCallback(async () => {
    try {
      const events = await chatbotService.getEvents();
      if (events && events.length > 0) {
        let response = `Here are the upcoming alumni events:\n\n`;
        events.forEach((event, index) => {
          const eventDate = new Date(event.eventDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
          
          response += `**${event.eventName}**\n`;
          response += `📅 **Date:** ${eventDate}\n`;
          response += `📍 **Venue:** ${event.venue}\n`;
          response += `🏢 **Chapter:** ${event.chapterName}\n`;
          response += `📝 **Description:** ${event.description}\n`;
          response += `📅 **Created:** ${new Date(event.createdAt).toLocaleDateString()}\n\n`;
        });
        response += `Would you like more details about any specific event?`;
        
        return {
          text: response,
          options: [
            { text: "👥 Show chapters", action: "chapters" },
            { text: "🔍 Search alumni", action: "search_alumni" },
            { text: "👤 Profile help", action: "profile_help" },
            { text: "❓ General help", action: "general_help" }
          ]
        };
      } else {
        return `I couldn't find any upcoming events at the moment. Please check back later or contact your chapter for more information.`;
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      return `I'm having trouble fetching event information right now. Please try again later.`;
    }
  }, []);

  // Get children details
  const getChildrenDetails = useCallback(async () => {
    try {
      // Check if user is logged in first
      if (!isUserLoggedIn()) {
        return {
          text: `I can help you with children details, but you need to login first to access this feature. Please login to continue.`,
          options: [
            { text: "🔐 Please login first", action: "login_required" },
            { text: "❓ General help", action: "general_help" }
          ]
        };
      }

      // Get user details and extract StudentId
      const userDetails = localStorage.getItem("UserLogDetails");
      if (!userDetails) {
        return {
          text: `I'm having trouble accessing your account information. Please login again.`,
          options: [
            { text: "🔐 Please login first", action: "login_required" },
            { text: "❓ General help", action: "general_help" }
          ]
        };
      }

      // AES decryption
      const AES_KEY = "7D3B8E69F1A84B7EA8A3F6792B44C926";
      const AES_IV = "A1B2C3D4E5F60718";
      
      const key = CryptoJS.enc.Utf8.parse(AES_KEY);
      const iv = CryptoJS.enc.Utf8.parse(AES_IV);
      const decrypted = CryptoJS.AES.decrypt(userDetails, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      
      const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
      const parsedUser = JSON.parse(decryptedText);
      const StudentId = parsedUser.StudentId;
      
      if (!StudentId) {
        return {
          text: `I couldn't find your student ID. Please login again to refresh your session.`,
          options: [
            { text: "🔐 Please login first", action: "login_required" },
            { text: "❓ General help", action: "general_help" }
          ]
        };
      }

      const children = await chatbotService.getChildrenDetails(StudentId);
      if (children && children.length > 0) {
        let response = `Here are your children details:\n\n`;
        children.forEach((child, index) => {
          response += `**${child.childName}**\n`;
          response += `👶 **Pet Name:** ${child.petName}\n`;
          response += `👤 **Gender:** ${child.genderName}\n`;
          response += `📅 **Date of Birth:** ${child.dob}\n`;
          response += `⭐ **Star:** ${child.star}\n`;
          response += `🔮 **Rasi:** ${child.rasi}\n\n`;
        });
        response += `You can manage these details in the "My Details" section of your profile.`;
        
        return {
          text: response,
          options: [
            { text: "👥 Show chapters", action: "chapters" },
            { text: "📅 Find events", action: "events" },
            { text: "👤 Profile help", action: "profile_help" },
            { text: "❓ General help", action: "general_help" }
          ]
        };
      } else {
        return {
          text: `You haven't added any children details yet. You can add them in the "My Details" section of your profile.`,
          options: [
            { text: "👥 Show chapters", action: "chapters" },
            { text: "📅 Find events", action: "events" },
            { text: "👤 Profile help", action: "profile_help" },
            { text: "❓ General help", action: "general_help" }
          ]
        };
      }
    } catch (error) {
      console.error('Error fetching children details:', error);
      return {
        text: `I'm having trouble fetching your children details right now. Please try again later or check your profile section.`,
        options: [
          { text: "👥 Show chapters", action: "chapters" },
          { text: "📅 Find events", action: "events" },
          { text: "❓ General help", action: "general_help" }
        ]
      };
    }
  }, [getUserInfo]);

  // Quick action handler
  const handleQuickAction = useCallback((action, context = {}) => {
    let message = "";
    switch (action) {
      case "search_alumni":
        message = "I'd like to search for alumni";
        break;
      case "find_events":
        message = "Show me upcoming events";
        break;
      case "chapter_info":
        message = "Tell me about chapters";
        break;
      case "profile_fields":
        message = "Explain the profile fields to me";
        break;
      case "update_profile":
        message = "How do I update my profile?";
        break;
      case "general_help":
        message = "I need general help";
        break;
      default:
        message = action;
    }
    sendMessage(message, context);
  }, [sendMessage]);

  const value = {
    ...state,
    openChatbot,
    closeChatbot,
    addMessage,
    setTyping,
    clearMessages,
    setQuickActions,
    setLoading,
    setError,
    updateConfig,
    sendMessage,
    handleQuickAction,
    generateAIResponse,
    getChapterDetails,
    getEventsDetails,
    getChildrenDetails,
    handleOptionClick,
    isUserLoggedIn,
    getUserInfo,
    refreshInitialMessage,
    checkAndRefreshAuth,
  };

  return (
    <ChatbotContext.Provider value={value}>
      {children}
    </ChatbotContext.Provider>
  );
};

// PropTypes validation
ChatbotProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Custom hook to use chatbot context
export const useChatbot = () => {
  const context = useContext(ChatbotContext);
  if (!context) {
    throw new Error('useChatbot must be used within a ChatbotProvider');
  }
  return context;
};

export default ChatbotContext;
